import React from 'react';
import { SearchOutlined, FilterOutlined } from '@ant-design/icons';
import { Container } from '@/components/ui/Container';
import Table from '@/components/ui/table/Table';
import { SliderTab } from '@/components/ui/SliderTab';
import SidePanel from '@/components/ui/SidePanel';
import BillingRequestDetailPanel from './BillingRequestDetailPanel';
import { useBillingRequests } from '@hooks/billing/useBillingRequests';
import ListBodyLayout from '@/components/layout/ListBodyLayout';
import {
    RolesSliderTabWrap,
    RolesSearchWrapper,
    RolesSearchInput,
    RolesFilterBtn,
    ErrorBlock,
} from './style';

const Billing = () => {
    const {
        total,
        pendingCount,
        acceptedCount,
        declinedCount,
        tableData,
        columns,
        searchText,
        setSearchText,
        statusTab,
        setStatusTab,
        STATUS_TAB,
        panelOpen,
        closePanel,
        openPanel,
        selectedRequest,
        selectedRequestTitle,
        canView,
        canRespond,
        respondMutation,
        requestsError,
    } = useBillingRequests();

    return (
        <ListBodyLayout
            title="Billing Requests"
            description="View and respond to payment requests"
            kpiCards={[
                {
                    title: 'Total Requests',
                    value: total,
                    change: { value: '', isPositive: true, text: 'all time' },
                },
                {
                    title: 'Pending',
                    value: pendingCount,
                    change: { value: '', isPositive: true, text: 'Awaiting response' },
                },
                {
                    title: 'Accepted',
                    value: acceptedCount,
                    change: { value: '', isPositive: true, text: 'Approved' },
                },
                {
                    title: 'Declined',
                    value: declinedCount,
                    change: { value: '', isPositive: true, text: 'Declined' },
                },
            ]}
        >
            {requestsError && (
                <ErrorBlock>
                    Unable to load billing requests. Check that the Billing API (GET /api/v1/billing/requests) is
                    available.
                </ErrorBlock>
            )}

            <Container
                $display="flex"
                $justifyContent="space-between"
                $alignItems="center"
                $flexWrap="wrap"
                $gap="12px"
                $marginBottom="16px"
            >
                <Container $display="flex" $alignItems="center" $minWidth="0">
                    <RolesSliderTabWrap>
                        <SliderTab
                            items={[
                                { key: STATUS_TAB.ALL, label: 'All' },
                                { key: STATUS_TAB.PENDING, label: 'Pending' },
                                { key: STATUS_TAB.ACCEPTED, label: 'Accepted' },
                                { key: STATUS_TAB.DECLINED, label: 'Declined' },
                            ]}
                            activeKey={statusTab}
                            onChange={setStatusTab}
                            fitContent
                        />
                    </RolesSliderTabWrap>
                </Container>
                <Container $display="flex" $alignItems="center" $gap="10px">
                    <RolesSearchWrapper>
                        <SearchOutlined />
                        <RolesSearchInput
                            placeholder="Search here"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </RolesSearchWrapper>
                    <RolesFilterBtn type="button">
                        <FilterOutlined />
                        Filter
                    </RolesFilterBtn>
                </Container>
            </Container>

            <Table
                title=""
                columns={columns}
                dataSource={tableData}
                searchValue={searchText}
                onSearchChange={setSearchText}
                showSearch={false}
                showFilter={false}
                showMore={false}
                hideToolbar
                pagination
                pageSize={10}
                $topMargin={false}
                $radius={12}
                onRow={(record) => ({
                    onClick: () => canView && openPanel(record.id),
                    style: { cursor: canView ? 'pointer' : 'default' },
                })}
            />

            <SidePanel open={panelOpen} onClose={closePanel} title={selectedRequestTitle} width="480px">
                <BillingRequestDetailPanel
                    request={selectedRequest}
                    canRespond={canRespond}
                    respondMutation={respondMutation}
                    onRespondSuccess={closePanel}
                />
            </SidePanel>
        </ListBodyLayout>
    );
};

export default Billing;
